
import { defineComponent, reactive, ref, toRefs, useContext, useRouter } from '@nuxtjs/composition-api'
import { useStore } from '@/useCases/useStore'
import { useValidation } from '@/useCases/validation'
import { type VForm } from '@/types/components'
import { setupAlertDialogCtx } from '@/components/dialogs/AlertDialog.vue'
import { ApiErrorType, PASSWORD_ATTEMPTS_EXCEEDED_MESSAGE } from '@/modules/error'

export default defineComponent({
  layout: 'plain',

  setup() {
    const store = useStore()
    const router = useRouter()
    const { i18n, $logger } = useContext()
    const { idRules, commonPasswordRules } = useValidation()
    const { isOpenedAlertDialog, openAlertDialog } = setupAlertDialogCtx()

    const formInput = reactive({ username: '', password: '' })
    const isInputValid = ref(false)
    const form = ref<VForm | null>(null)

    const alertTitle = ref('')
    const alertMessage = ref('')

    const isLoading = ref(false)
    const isShowedPassword = ref(false)

    const signIn = async () => {
      if (!form.value?.validate()) return
      isLoading.value = true
      try {
        await store.dispatch('user/signIn', formInput)
        await router.push('/')
      } catch (err) {
        $logger.error('signIn', err)
        if (
          err &&
          typeof err === 'object' &&
          'code' in err &&
          err.code === ApiErrorType.NotAuthorizedException &&
          'message' in err &&
          typeof err.message === 'string' &&
          err.message === PASSWORD_ATTEMPTS_EXCEEDED_MESSAGE
        ) {
          // パスワードの試行回数を超えた場合
          alertTitle.value = i18n.tc('alert.passwordAttemptsExceeded.title', 1)
          alertMessage.value = i18n.tc('alert.passwordAttemptsExceeded.message', 1)
        } else {
          alertTitle.value = i18n.tc('alert.userNotFound.title', 1)
          alertMessage.value = i18n.tc('alert.userNotFound.message', 1)
        }
        openAlertDialog()
        if (
          err &&
          typeof err === 'object' &&
          'code' in err &&
          typeof err.code === 'string' &&
          err.code !== ApiErrorType.UserNotFoundException &&
          err.code !== ApiErrorType.NotAuthorizedException
        ) {
          // id, password 間違い以外の場合のみ sentry に送信
          $logger.captureException(err)
        }
      } finally {
        isLoading.value = false
      }
    }

    store.dispatch('user/signOut')

    return {
      idRules,
      commonPasswordRules,
      isOpenedAlertDialog,
      ...toRefs(formInput),
      isInputValid,
      form,
      alertTitle,
      alertMessage,
      signIn,
      isLoading,
      isShowedPassword
    }
  }
})
